import { PageHeader } from '../components/office';
import { useState, useEffect } from 'react';
import { staffApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Switch } from '@verlo/shared/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@verlo/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@verlo/shared/components/ui/table';
import { Avatar, AvatarFallback } from '@verlo/shared/components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, RefreshCw, Users, Mail, Phone } from 'lucide-react';
import { useAuthoringSite } from '../contexts/SiteContext';
import { SearchField } from '@verlo/shared/components/ui/search-field';

const roleColors = {
  Manager: 'bg-signal-info-tint text-signal-info',
  Server: 'bg-signal-info-tint text-signal-info',
  Bartender: 'bg-signal-ok-tint text-signal-ok',
  Chef: 'bg-signal-warn-tint text-signal-warn',
  Host: 'bg-signal-stop-tint text-signal-stop',
};

const StaffPage = () => {
  // Which venue a new row belongs to. With more than one site and none chosen, there is no
  // honest answer, so the add control says so rather than firing a request the server will
  // rightly refuse ("pass ?site=<id>") with nowhere in this page to pass it.
  const authoring = useAuthoringSite();
  const { hasPlatformPermission, isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Server',
    hourly_rate: '',
  });

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await staffApi.getAll(!showInactive);
      setStaff(res.data);
    } catch (err) {
      toast.error("Couldn't load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [showInactive]);

  const filteredStaff = staff.filter((member) =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = staff.filter((s) => s.is_active).length;

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'Server',
      hourly_rate: '',
    });
    setEditingStaff(null);
  };

  const openEditDialog = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone,
      role: member.role,
      hourly_rate: member.hourly_rate.toString(),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      hourly_rate: parseFloat(formData.hourly_rate) || 0,
    };

    try {
      if (editingStaff) {
        await staffApi.update(editingStaff.id, submitData);
        toast.success('Staff member updated');
      } else {
        await staffApi.create(submitData, authoring.site);
        toast.success('Staff member added');
      }
      setDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save staff member");
    }
  };

  const handleToggleStatus = async (staffId, currentStatus) => {
    try {
      await staffApi.toggleStatus(staffId, !currentStatus);
      toast.success(`Staff member ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchStaff();
    } catch (err) {
      toast.error("Couldn't update status");
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await staffApi.delete(staffId);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (err) {
      toast.error("Couldn't delete staff member");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  };

  const getInitials = (name) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      {/* Header */}
      <PageHeader
        title="Staff"
        purpose="Everyone on the team: role, rate and how to reach them."
        actions={(<>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
          </div>
          <Button variant="outline" onClick={fetchStaff} data-testid="refresh-staff-btn">
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
          {isAdmin() && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-accent" onClick={resetForm} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="add-staff-btn">
                  <Plus size={18} className="mr-2" />
                  Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="staff-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="staff-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="staff-phone"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(v) => setFormData({ ...formData, role: v })}
                      >
                        <SelectTrigger data-testid="staff-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Manager">Manager</SelectItem>
                          <SelectItem value="Server">Server</SelectItem>
                          <SelectItem value="Bartender">Bartender</SelectItem>
                          <SelectItem value="Chef">Chef</SelectItem>
                          <SelectItem value="Host">Host</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hourly_rate">Hourly Rate</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                        data-testid="staff-rate"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    {editingStaff && hasPlatformPermission('orgs.assist') && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => {
                          handleDelete(editingStaff.id);
                          setDialogOpen(false);
                        }}
                      >
                        <Trash2 size={16} className="mr-2" />
                        Delete
                      </Button>
                    )}
                    <Button type="submit" className="btn-primary" data-testid="save-staff-btn">
                      {editingStaff ? 'Update' : 'Add'} Staff
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      
        </>)}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Staff</p>
              <p className="text-[24px] leading-none font-mono">{staff.length}</p>
            </div>
            <Users className="text-navy" size={24} />
          </div>
        </Card>
        <Card className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-[24px] leading-none text-signal-ok font-mono">{activeCount}</p>
            </div>
            <Users className="text-signal-ok" size={24} />
          </div>
        </Card>
        <Card className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Inactive</p>
              <p className="text-[24px] leading-none text-ink-faint font-mono">{staff.length - activeCount}</p>
            </div>
            <Users className="text-ink-faint" size={24} />
          </div>
        </Card>
      </div>

      {/* Search */}
      <SearchField placeholder="Search staff by name, email, or role..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="staff-search" />

      {/* Staff Table */}
      <Card>
        <CardContent className="p-0">
          {filteredStaff.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Hourly Rate</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.map((member) => (
                  <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-navy text-white">
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{member.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail size={14} className="text-muted-foreground" />
                          {member.email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone size={14} />
                          {member.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={roleColors[member.role] || 'bg-paper text-ink'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(member.hourly_rate)}/hr</TableCell>
                    <TableCell>
                      {isAdmin() ? (
                        <Switch
                          checked={member.is_active}
                          onCheckedChange={() => handleToggleStatus(member.id, member.is_active)}
                          data-testid={`toggle-status-${member.id}`}
                        />
                      ) : (
                        <Badge variant={member.is_active ? 'default' : 'secondary'}>
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(member)}
                          data-testid={`edit-staff-${member.id}`}
                        >
                          <Edit size={14} className="mr-2" />
                          Edit
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No staff members found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffPage;
